import { fromUrl } from 'geotiff';
import * as THREE from 'three';
import {
  TERRAIN_UNDERSIDE_COLOR,
  TERRAIN_DEBUG_PROBE,
  TERRAIN_LOG_NORMALS,
} from '../config/terrainAssets';
import {
  fetchStitchWebMercatorTiles,
  logXyzOrthoUvAlignmentProbe,
  nativeBBoxToWebMercatorBounds,
  nativeBBoxToWebMercatorBoundsCornerOnly,
  pickZoomForWebMercatorBBox,
  uvFromNativeEn,
} from './rasterOrthoTiles';

const PERF_MARK_START = 'raster-cog-load-start';
const PERF_MARK_END = 'raster-cog-load-end';
const PERF_MEASURE = 'raster-cog-load';

/** Log object-space normal stats after computeVertexNormals (Y-up terrain: avg Y should be positive). */
function logTerrainNormalProbe(geometry, context) {
  const attr = geometry?.attributes?.normal;
  if (!attr) {
    // eslint-disable-next-line no-console
    console.warn('[rasterTerrain] probe: no normal attribute', context);
    return;
  }
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  const step = Math.max(1, Math.floor(attr.count / 400));
  let n = 0;
  for (let i = 0; i < attr.count; i += step) {
    sumX += attr.getX(i);
    sumY += attr.getY(i);
    sumZ += attr.getZ(i);
    n += 1;
  }
  const ax = sumX / n;
  const ay = sumY / n;
  const az = sumZ / n;
  // eslint-disable-next-line no-console
  console.info(
    `[rasterTerrain] normal probe (${context}): avg normal (${ax.toFixed(3)}, ${ay.toFixed(3)}, ${az.toFixed(3)}) · samples=${n} · expect avgY>0 for +Y-up top surface`,
  );
}

/** Linear RGB 0–1 for underside shader uniform */
function hexToRgbVector3(hex) {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  return new THREE.Vector3(r, g, b);
}


/**
 * Wrap fetch so tiling_cog_proxy JSON error bodies are visible in the console.
 * geotiff.js only reports "Request failed" / AggregateError otherwise.
 * @param {typeof fetch} [baseFetch]
 * @returns {typeof fetch}
 */
function createTilingCogProxyFetch(baseFetch = fetch) {
  return async (resource, options) => {
    const res = await baseFetch(resource, options);
    if (!res.ok) {
      const url = typeof resource === 'string' ? resource : resource?.url || '';
      const idMatch = url.match(/[?&]id=(\d+)/);
      const assetHint = idMatch ? ` asset id=${idMatch[1]}` : '';
      try {
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        const text = await res.clone().text();
        if (ct.includes('application/json')) {
          try {
            const j = JSON.parse(text);
            const line = [j.error, j.message, j.hint, j.type].filter(Boolean).join(' — ');
            // eslint-disable-next-line no-console
            console.error(`[rasterTerrain] tiling_cog_proxy HTTP ${res.status}${assetHint}:`, line || text, j);
          } catch {
            // eslint-disable-next-line no-console
            console.error(`[rasterTerrain] tiling_cog_proxy HTTP ${res.status}${assetHint}:`, text.slice(0, 800));
          }
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `[rasterTerrain] tiling_cog_proxy HTTP ${res.status}${assetHint} (non-JSON):`,
            text.slice(0, 500),
          );
        }
      } catch {
        /* ignore */
      }
    }
    return res;
  };
}

/**
 * @param {{ easting: number, northing: number }} collarWorld
 * @param {number} [marginMeters=250]
 */
export function computeRoiBounds(collarWorld, marginMeters = 250) {
  if (!collarWorld || typeof collarWorld.easting !== 'number' || typeof collarWorld.northing !== 'number') {
    return null;
  }
  const m = marginMeters;
  return {
    minX: collarWorld.easting - m,
    maxX: collarWorld.easting + m,
    minY: collarWorld.northing - m,
    maxY: collarWorld.northing + m,
  };
}

/**
 * DEM fetch bbox in native CRS: union of all **collar** positions (first point of each trace) plus margin.
 * Only collar positions are used (not underground trace points) so the ROI stays focused on the surface.
 * When only one hole (or no points), falls back to {@link computeRoiBounds} around the selected collar.
 *
 * @param {{ easting: number, northing: number, elevation?: number }} collarWorld selected hole collar (API frame)
 * @param {Array<{ hole_id: string, points?: Array<{ x: number, y: number, z: number }> }>|null|undefined} multiTraceData
 * @param {number} [marginMeters=250]
 */
export function computeTerrainRoiBounds(collarWorld, multiTraceData, marginMeters = 250) {
  if (!collarWorld || typeof collarWorld.easting !== 'number' || typeof collarWorld.northing !== 'number') {
    return null;
  }
  const m = marginMeters;
  const holes = multiTraceData || [];
  if (holes.length <= 1) {
    return computeRoiBounds(collarWorld, marginMeters);
  }
  let minE = collarWorld.easting;
  let maxE = collarWorld.easting;
  let minN = collarWorld.northing;
  let maxN = collarWorld.northing;
  for (const hole of holes) {
    const pts = hole.points;
    if (!pts?.length) continue;
    const collar = pts[0];
    const e = collarWorld.easting + collar.x;
    const n = collarWorld.northing + collar.y;
    minE = Math.min(minE, e);
    maxE = Math.max(maxE, e);
    minN = Math.min(minN, n);
    maxN = Math.max(maxN, n);
  }
  return {
    minX: minE - m,
    maxX: maxE + m,
    minY: minN - m,
    maxY: maxN + m,
  };
}

/**
 * Choose gridSegments so each cell stays near a target resolution (default ~10 m/cell),
 * clamped to [24, 127] (dim 25–128 in buildDemTerrainMesh).
 * @param {{ minX: number, maxX: number, minY: number, maxY: number }} roi
 * @param {number} [targetCellM=10]
 */
export function computeAdaptiveGridSegments(roi, targetCellM = 10) {
  const spanE = roi.maxX - roi.minX;
  const spanN = roi.maxY - roi.minY;
  const maxSpan = Math.max(spanE, spanN, 1);
  return Math.min(127, Math.max(24, Math.round(maxSpan / targetCellM)));
}

/**
 * Intersect axis-aligned bbox with GeoTIFF image extent [minX, minY, maxX, maxY].
 * @returns {[number,number,number,number]|null}
 */
export function clipBBoxToImage(roi, imageBBox) {
  if (!roi || !imageBBox || imageBBox.length < 4) return null;
  const [ix0, iy0, ix1, iy1] = imageBBox;
  const x0 = Math.max(roi.minX, ix0);
  const y0 = Math.max(roi.minY, iy0);
  const x1 = Math.min(roi.maxX, ix1);
  const y1 = Math.min(roi.maxY, iy1);
  if (x0 >= x1 || y0 >= y1) return null;
  return [x0, y0, x1, y1];
}

/**
 * @param {object} assetBounds API bounds { minX, maxX, minY, maxY }
 * @param {{ easting: number, northing: number }} collarWorld
 */
export function assetBoundsContainCollar(assetBounds, collarWorld) {
  if (!assetBounds || !collarWorld) return false;
  const { minX, maxX, minY, maxY } = assetBounds;
  if (minX == null || maxX == null || minY == null || maxY == null) return false;
  const { easting, northing } = collarWorld;
  return easting >= minX && easting <= maxX && northing >= minY && northing <= maxY;
}

/**
 * Pick default raster asset that contains collar in native bounds, else first item.
 */
export function pickDefaultAssetId(assets, collarWorld) {
  if (!assets?.length) return null;
  if (!collarWorld) return assets[0].id;
  const hit = assets.find((a) => assetBoundsContainCollar(a.bounds, collarWorld));
  return (hit || assets[0]).id;
}

/** @param {Array<{ id: number, bandCount?: number|null, bounds?: object }>} assets */
export function filterDemAssets(assets) {
  return (assets || []).filter((a) => a.bandCount === 1);
}

/** @param {Array<{ id: number, bandCount?: number|null, bounds?: object }>} assets */
export function filterOrthoAssets(assets) {
  return (assets || []).filter((a) => (a.bandCount ?? 0) >= 3);
}

/**
 * Default DEM asset (single-band) containing collar, else first DEM.
 * @returns {number|null}
 */
export function pickDefaultDemAssetId(assets, collarWorld) {
  const dems = filterDemAssets(assets);
  if (!dems.length) {
    if (assets?.length) {
      // eslint-disable-next-line no-console
      console.warn('[rasterTerrain] No single-band DEM in catalog; add a 1-band elevation COG for terrain.');
    }
    return null;
  }
  if (!collarWorld) return dems[0].id;
  const hit = dems.find((a) => assetBoundsContainCollar(a.bounds, collarWorld));
  return (hit || dems[0]).id;
}

/**
 * Default orthophoto asset (3+ bands) containing collar, else first ortho.
 * @returns {number|null}
 */
export function pickDefaultOrthoAssetId(assets, collarWorld) {
  const orthos = filterOrthoAssets(assets);
  if (!orthos.length) return null;
  if (!collarWorld) return orthos[0].id;
  const hit = orthos.find((a) => assetBoundsContainCollar(a.bounds, collarWorld));
  return (hit || orthos[0]).id;
}

/**
 * BFF ortho window (native bbox + size) → RGBA DataTexture same size as DEM grid.
 * @returns {Promise<THREE.DataTexture|null>}
 */
async function loadOrthophotoWarpWindowTexture(url, fetchHeaders, sx, sy) {
  const res = await fetch(url, { headers: fetchHeaders || {} });
  if (!res.ok) {
    // eslint-disable-next-line no-console
    console.info('[rasterTerrain] orthophoto warp window HTTP', res.status, url.split('?')[0]);
    return null;
  }
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (ct.includes('application/json')) {
    // eslint-disable-next-line no-console
    console.info('[rasterTerrain] orthophoto warp window returned JSON (error body), skipping');
    return null;
  }
  const blob = await res.blob();
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = sx;
  canvas.height = sy;
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) {
    bmp.close?.();
    return null;
  }
  ctx2d.drawImage(bmp, 0, 0, sx, sy);
  bmp.close?.();
  const { data } = ctx2d.getImageData(0, 0, sx, sy);
  const rgba = new Uint8Array(sx * sy * 4);
  // PNG row 0 = canvas top: contract is GDAL north-up (row 0 = maxY), matching mesh j=0 = north.
  rgba.set(data);
  const tex = new THREE.DataTexture(rgba, sx, sy, THREE.RGBAFormat);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Load a DEM window from a COG URL and build a terrain mesh in anchor-relative Three.js space
 * (X east, Y up, Z north — matches DrillHole3DViewer trace mapping).
 * Vertices are (east − Ox, elev − Oz, north − Oy) with anchor (Ox,Oy,Oz) = scene origin (often selected collar;
 * for multiple holes, use a shared anchor such as the trace-set bbox center so DEM and traces share one frame).
 *
 * @param {object} opts
 * @param {string} opts.cogUrl COG URL (e.g. API proxy URL); must support HTTP Range
 * @param {Record<string, string>} [opts.fetchHeaders] headers for every ranged GET (e.g. Authorization)
 * @param {[number,number,number,number]} opts.bbox native CRS [minX, minY, maxX, maxY]
 * @param {{ easting: number, northing: number, elevation?: number }} opts.anchorWorld scene origin in native CRS / viewer axes
 * @param {number} [opts.gridSegments=48] quads per side (grid is segments+1 verts)
 * @param {string|null} [opts.orthoCogUrl] optional ortho COG fallback (same CRS as DEM)
 * @param {boolean} [opts.applyOrthoTexture=false] when true and orthoCogUrl set, drape RGB on mesh
 * @param {number} [opts.nativeEpsg=26908] native CRS EPSG for DEM bbox / UV projection to Web Mercator tiles
 * @param {number|null} [opts.orthoAssetId] orthophoto asset id for XYZ BFF
 * @param {boolean} [opts.orthoUseXyzTiles=false] try RasterTileApi tiles via getRasterTileUrl first
 * @param {{ hasTileCog?: boolean, tileMinZoom?: number|null, tileMaxZoom?: number|null }|null} [opts.orthoTileMeta]
 * @param {(assetId: number, z: number, x: number, y: number) => string} [opts.getRasterTileUrl]
 * @param {boolean} [opts.orthoXyzTmsY=false] TMS tile row for XYZ URLs
 * @param {boolean} [opts.orthoUseServerWarpWindow=false] try GDAL warp BFF before XYZ/COG
 * @param {(assetId: number, bounds: {minX:number,minY:number,maxX:number,maxY:number}, srid: number, width: number, height: number) => string} [opts.getOrthophotoWindowUrl]
 * @returns {Promise<THREE.Mesh|null>}
 */
export async function buildDemTerrainMesh({
  cogUrl,
  fetchHeaders,
  bbox,
  anchorWorld,
  gridSegments = 48,
  orthoCogUrl = null,
  applyOrthoTexture = false,
  nativeEpsg = 26908,
  orthoAssetId = null,
  orthoUseXyzTiles = false,
  orthoTileMeta = null,
  getRasterTileUrl = null,
  orthoXyzTmsY = false,
  orthoUseServerWarpWindow = false,
  getOrthophotoWindowUrl = null,
}) {
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(PERF_MARK_START);
  }

  const cogFetch = createTilingCogProxyFetch();
  const fromOpts = { headers: fetchHeaders || {}, fetch: cogFetch };

  const tiff = await fromUrl(cogUrl, fromOpts);
  const first = await tiff.getImage();
  const [, demResY] = first.getResolution();
  // readRasters({bbox}) returns rows south-to-north (row 0 = minY);
  // vertex loop goes north-to-south (j=0 = maxY), so always flip.
  const demFlipRows = true;
  const imgBBox = first.getBoundingBox();
  const clipped = clipBBoxToImage(
    { minX: bbox[0], minY: bbox[1], maxX: bbox[2], maxY: bbox[3] },
    imgBBox,
  );
  if (!clipped) {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(PERF_MARK_END);
    }
    return null;
  }

  const dim = Math.max(2, Math.min(128, gridSegments + 1));
  const arrays = await tiff.readRasters({
    bbox: clipped,
    width: dim,
    height: dim,
    samples: [0],
    interleave: false,
  });

  const dem = arrays[0];
  if (!dem || dem.length < dim * dim) {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(PERF_MARK_END);
    }
    return null;
  }

  const gdalNoData = first.getGDALNoData();
  const noData =
    gdalNoData !== null && gdalNoData !== undefined && !Number.isNaN(Number(gdalNoData))
      ? Number(gdalNoData)
      : null;

  const [minX, minY, maxX, maxY] = clipped;
  const sx = dim;
  const sy = dim;
  const ce = anchorWorld.easting;
  const cn = anchorWorld.northing;
  const cz = anchorWorld.elevation ?? 0;

  // eslint-disable-next-line no-console
  console.info('[rasterTerrain] DEM resY:', demResY, 'flipRows:', demFlipRows,
    'corner[0,0]:', dem[0], 'corner[sy-1,sx-1]:', dem[(sy - 1) * sx + (sx - 1)]);

  const positions = new Float32Array(sx * sy * 3);
  const uvs = new Float32Array(sx * sy * 2);
  let idx = 0;
  let uvIdx = 0;
  for (let j = 0; j < sy; j++) {
    const tn = sy > 1 ? j / (sy - 1) : 0;
    const north = maxY - tn * (maxY - minY);
    for (let i = 0; i < sx; i++) {
      const te = sx > 1 ? i / (sx - 1) : 0;
      const east = minX + te * (maxX - minX);
      const demJ = demFlipRows ? (sy - 1 - j) : j;
      let v = dem[demJ * sx + i];
      if (noData !== null && (v === noData || (typeof v === 'number' && Math.abs(v - noData) < 1e-6))) {
        v = cz;
      }
      if (typeof v !== 'number' || Number.isNaN(v)) {
        v = cz;
      }
      positions[idx] = east - ce;
      positions[idx + 1] = v - cz;
      positions[idx + 2] = north - cn;
      idx += 3;
      uvs[uvIdx] = te;
      uvs[uvIdx + 1] = 1 - tn;
      uvIdx += 2;
    }
  }

  const indices = [];
  for (let j = 0; j < sy - 1; j++) {
    for (let i = 0; i < sx - 1; i++) {
      const a = j * sx + i;
      const b = a + 1;
      const c = a + sx;
      const d = c + 1;
      // CCW from +Y: normals must point +Y (sky) so underside test dot(n, viewDir)<0 works from below.
      indices.push(a, b, c, b, d, c);
    }
  }

  let mapTexture = null;
  /** @type {{ minMx: number, maxMx: number, northMy: number, southMy: number }|null} */
  let xyzStitchMercator = null;
  /** @type {'none'|'warp'|'xyz'|'cog'} */
  let orthoSource = 'none';

  if (applyOrthoTexture) {
    const tryXyz =
      orthoUseXyzTiles &&
      orthoAssetId != null &&
      orthoTileMeta?.hasTileCog &&
      typeof getRasterTileUrl === 'function';

    // eslint-disable-next-line no-console
    console.groupCollapsed('[rasterTerrain] orthophoto — inputs & path gates');
    // eslint-disable-next-line no-console
    console.info('DEM readRasters window (native, clipped to GeoTIFF)', {
      clipped: { minX: clipped[0], minY: clipped[1], maxX: clipped[2], maxY: clipped[3] },
      nativeEpsg,
      grid: { sx, sy },
      anchorWorld,
      imgExtentFromDEM: imgBBox,
    });
    // eslint-disable-next-line no-console
    console.info('ortho path flags', {
      orthoUseXyzTiles,
      orthoAssetId,
      orthoTileMeta,
      hasGetRasterTileUrl: typeof getRasterTileUrl === 'function',
      tryXyz,
      orthoUseServerWarpWindow,
      orthoXyzTmsY,
    });
    // eslint-disable-next-line no-console
    console.groupEnd();

    if (
      orthoUseServerWarpWindow &&
      orthoAssetId != null &&
      typeof getOrthophotoWindowUrl === 'function'
    ) {
      try {
        const warpUrl = getOrthophotoWindowUrl(
          orthoAssetId,
          { minX: clipped[0], minY: clipped[1], maxX: clipped[2], maxY: clipped[3] },
          nativeEpsg,
          sx,
          sy,
        );
        const warpTex = await loadOrthophotoWarpWindowTexture(warpUrl, fetchHeaders, sx, sy);
        if (warpTex) {
          mapTexture = warpTex;
          orthoSource = 'warp';
          // eslint-disable-next-line no-console
          console.info('[rasterTerrain] orthophoto path: warp window', sx, 'x', sy);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[rasterTerrain] orthophoto warp window failed, trying XYZ/COG:', e);
      }
    }

    if (!mapTexture && tryXyz) {
      try {
        const wmbCorner = nativeBBoxToWebMercatorBoundsCornerOnly(clipped, nativeEpsg);
        const wmb = nativeBBoxToWebMercatorBounds(clipped, nativeEpsg);
        // eslint-disable-next-line no-console
        console.groupCollapsed('[rasterTerrain] orthophoto XYZ — UTM bbox → Web Mercator (tile pick only)');
        // eslint-disable-next-line no-console
        console.info('corner-only WM AABB', wmbCorner);
        // eslint-disable-next-line no-console
        console.info('sampled-edge WM AABB (used for z / tx / ty)', wmb);
        // eslint-disable-next-line no-console
        console.groupEnd();
        const { z, range } = pickZoomForWebMercatorBBox(
          wmb.minMx,
          wmb.maxMx,
          wmb.minMy,
          wmb.maxMy,
          {
            tileMinZoom: orthoTileMeta.tileMinZoom,
            tileMaxZoom: orthoTileMeta.tileMaxZoom,
            maxTiles: 36,
          },
        );
        // eslint-disable-next-line no-console
        console.info('[rasterTerrain] pickZoomForWebMercatorBBox result', {
          z,
          range,
          tileMinZoom: orthoTileMeta.tileMinZoom,
          tileMaxZoom: orthoTileMeta.tileMaxZoom,
          maxTiles: 36,
        });
        const stitched = await fetchStitchWebMercatorTiles({
          z,
          range,
          assetId: orthoAssetId,
          getTileUrl: getRasterTileUrl,
          fetchHeaders: fetchHeaders || {},
          concurrency: 8,
          tmsTileY: orthoXyzTmsY,
        });
        if (stitched) {
          const { canvas, minMx, maxMx, northMy, southMy } = stitched;
          xyzStitchMercator = { minMx, maxMx, northMy, southMy };
          mapTexture = new THREE.CanvasTexture(canvas);
          mapTexture.colorSpace = THREE.SRGBColorSpace;
          mapTexture.minFilter = THREE.LinearFilter;
          mapTexture.magFilter = THREE.LinearFilter;
          mapTexture.wrapS = THREE.ClampToEdgeWrapping;
          mapTexture.wrapT = THREE.ClampToEdgeWrapping;
          // Default true: canvas row 0 (north) must match Three/WebGL map sampling (same idea as ImageTexture).
          // flipY=false breaks N–S alignment vs uvFromNativeEn and looks “scaled” wrong under clamp.
          mapTexture.flipY = true;
          mapTexture.needsUpdate = true;
          orthoSource = 'xyz';
          // eslint-disable-next-line no-console
          console.groupCollapsed('[rasterTerrain] orthophoto XYZ — Three.js texture vs DEM grid');
          // eslint-disable-next-line no-console
          console.info('CanvasTexture', {
            flipY: mapTexture.flipY,
            canvasPx: { w: canvas.width, h: canvas.height },
            demGridVerts: { sx, sy },
            note: 'UVs map native ground → stitch WM extent; texture is Web Mercator mosaic',
          });
          // eslint-disable-next-line no-console
          console.groupEnd();
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[rasterTerrain] Orthophoto XYZ tiles failed, trying COG if available:', e);
      }
    }

    if (!mapTexture && orthoCogUrl) {
      try {
        const orthoTiff = await fromUrl(orthoCogUrl, fromOpts);
        const orthoImg = await orthoTiff.getImage();
        const orthoBBox = orthoImg.getBoundingBox();
        const orthoOverlap = clipBBoxToImage(
          { minX: clipped[0], minY: clipped[1], maxX: clipped[2], maxY: clipped[3] },
          orthoBBox,
        );
        if (orthoOverlap) {
          const oArrays = await orthoImg.readRasters({
            bbox: clipped,
            width: sx,
            height: sy,
            samples: [0, 1, 2],
            interleave: false,
          });
          const r = oArrays[0];
          const g = oArrays[1];
          const b = oArrays[2];
          if (r && g && b && r.length >= sx * sy) {
            const rgba = new Uint8Array(sx * sy * 4);
            // readRasters row 0 = minY (south); mesh j=0 = maxY (north), same demFlipRows as DEM heights.
            for (let j = 0; j < sy; j++) {
              for (let i = 0; i < sx; i++) {
                const demJ = demFlipRows ? sy - 1 - j : j;
                const p = demJ * sx + i;
                const o = (j * sx + i) * 4;
                let rv = r[p];
                let gv = g[p];
                let bv = b[p];
                if (typeof rv !== 'number' || Number.isNaN(rv)) rv = 0;
                if (typeof gv !== 'number' || Number.isNaN(gv)) gv = 0;
                if (typeof bv !== 'number' || Number.isNaN(bv)) bv = 0;
                rgba[o] = Math.max(0, Math.min(255, Math.round(rv)));
                rgba[o + 1] = Math.max(0, Math.min(255, Math.round(gv)));
                rgba[o + 2] = Math.max(0, Math.min(255, Math.round(bv)));
                rgba[o + 3] = 255;
              }
            }
            mapTexture = new THREE.DataTexture(rgba, sx, sy, THREE.RGBAFormat);
            mapTexture.colorSpace = THREE.SRGBColorSpace;
            mapTexture.minFilter = THREE.LinearFilter;
            mapTexture.magFilter = THREE.LinearFilter;
            mapTexture.wrapS = THREE.ClampToEdgeWrapping;
            mapTexture.wrapT = THREE.ClampToEdgeWrapping;
            mapTexture.flipY = false;
            mapTexture.needsUpdate = true;
            orthoSource = 'cog';
            // eslint-disable-next-line no-console
            console.info('[rasterTerrain] orthophoto path: COG readRasters', sx, 'x', sy);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          '[rasterTerrain] Orthophoto COG draping failed, using solid terrain (see tiling_cog_proxy logs):',
          e,
        );
      }
    }

    if (applyOrthoTexture && orthoSource === 'none') {
      // eslint-disable-next-line no-console
      console.info(
        '[rasterTerrain] orthophoto: none (warp off or failed, XYZ unavailable or failed, COG missing/failed)',
        { tryXyz, orthoUseServerWarpWindow },
      );
    }
  }

  if (xyzStitchMercator) {
    let uvIdx = 0;
    const { minMx, maxMx, northMy, southMy } = xyzStitchMercator;
    for (let j = 0; j < sy; j++) {
      const tn = sy > 1 ? j / (sy - 1) : 0;
      const north = maxY - tn * (maxY - minY);
      for (let i = 0; i < sx; i++) {
        const te = sx > 1 ? i / (sx - 1) : 0;
        const east = minX + te * (maxX - minX);
        const { u, v } = uvFromNativeEn(east, north, nativeEpsg, minMx, maxMx, northMy, southMy);
        uvs[uvIdx] = u;
        // Stitch space: north → v≈0, south → v≈1. With CanvasTexture.flipY=true, use mesh V = v so N–S
        // matches DEM (same net as debug “Flip V”: repeat.y=-1, offset.y=1 on the old 1−v UVs).
        uvs[uvIdx + 1] = v;
        uvIdx += 2;
      }
    }
    logXyzOrthoUvAlignmentProbe({
      nativeEpsg,
      minX,
      minY,
      maxX,
      maxY,
      sx,
      sy,
      minMx,
      maxMx,
      northMy,
      southMy,
    });
  }

  if (mapTexture) {
    mapTexture.offset.set(0, 0);
    mapTexture.repeat.set(1, 1);
    mapTexture.center.set(0.5, 0.5);
    mapTexture.rotation = 0;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  if (TERRAIN_DEBUG_PROBE !== 0 || TERRAIN_LOG_NORMALS) {
    logTerrainNormalProbe(geometry, 'after computeVertexNormals');
  }

  const material = new THREE.MeshStandardMaterial({
    color: mapTexture ? 0xffffff : 0x3d4f3f,
    map: mapTexture,
    roughness: mapTexture ? 0.88 : 0.92,
    metalness: 0.05,
    flatShading: false,
    side: THREE.DoubleSide,
    transparent: Boolean(mapTexture),
    depthWrite: !mapTexture,
  });

  if (mapTexture) {
    const undersideRgb = hexToRgbVector3(TERRAIN_UNDERSIDE_COLOR);
    material.userData.terrainDebugProbe = { value: TERRAIN_DEBUG_PROBE };
    material.userData.terrainUndersideEnabled = { value: 1 };
    material.userData.terrainWireframe = { value: 0 };
    material.onBeforeCompile = (parameters) => {
      parameters.uniforms.uTerrainUndersideColor = { value: undersideRgb };
      parameters.uniforms.uTerrainDebugProbe = material.userData.terrainDebugProbe;
      parameters.uniforms.uTerrainUndersideEnabled = material.userData.terrainUndersideEnabled;
      parameters.uniforms.uTerrainWireframe = material.userData.terrainWireframe;
      parameters.vertexShader = parameters.vertexShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldNormal;
varying vec3 vTerrainWorldPos;`,
      );
      parameters.vertexShader = parameters.vertexShader.replace(
        '#include <displacementmap_vertex>',
        `#include <displacementmap_vertex>
	vTerrainWorldNormal = inverseTransformDirection( normalize( vNormal ), viewMatrix );
	vTerrainWorldPos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;`,
      );
      parameters.fragmentShader = parameters.fragmentShader.replace(
        '#include <common>',
        `#include <common>
varying vec3 vTerrainWorldNormal;
varying vec3 vTerrainWorldPos;
uniform vec3 uTerrainUndersideColor;
uniform float uTerrainDebugProbe;
uniform float uTerrainUndersideEnabled;
uniform float uTerrainWireframe;`,
      );
      // Smooth heightfields share ~+Y vertex normals on both sides, so dot(n,view) does not flip top vs bottom.
      // Filled triangles: use gl_FrontFacing (false = back face = underside when orbiting below).
      // Wireframe (LINE): gl_FrontFacing is undefined per spec; keep dot<0 as best-effort fallback.
      parameters.fragmentShader = parameters.fragmentShader.replace(
        '#include <output_fragment>',
        `vec3 terrainViewDir = normalize( cameraPosition - vTerrainWorldPos );
	float terrainNdV = dot( vTerrainWorldNormal, terrainViewDir );
	bool terrainBackFace = !gl_FrontFacing;
	bool terrainWantUnderside = ( uTerrainWireframe > 0.5 )
		? ( terrainBackFace || ( terrainNdV < 0.0 ) )
		: terrainBackFace;
	if ( uTerrainDebugProbe > 1.5 ) {
		outgoingLight = terrainWantUnderside ? vec3( 1.0, 0.25, 0.25 ) : vec3( 0.25, 1.0, 0.25 );
		diffuseColor = vec4( 1.0 );
	} else if ( uTerrainDebugProbe > 0.5 ) {
		outgoingLight = vTerrainWorldNormal * 0.5 + 0.5;
		diffuseColor = vec4( 1.0 );
	} else if ( uTerrainUndersideEnabled > 0.5 && terrainWantUnderside ) {
		outgoingLight = uTerrainUndersideColor;
		diffuseColor = vec4( uTerrainUndersideColor, 1.0 );
	}
	#include <output_fragment>`,
      );
    };
    material.customProgramCacheKey = () =>
      `terrainUnderside_wn14_${TERRAIN_UNDERSIDE_COLOR}`;
  }

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'raster_dem_terrain';
  mesh.receiveShadow = true;
  mesh.userData.terrainUndersideFill = Boolean(mapTexture);
  mesh.userData.terrainDebugInfo = {
    demResY,
    demFlipRows,
    orthoSource,
    orthoFlipY: mapTexture?.flipY ?? null,
  };

  // eslint-disable-next-line no-console
  console.info('[rasterTerrain] mesh ready — terrainDebugInfo', mesh.userData.terrainDebugInfo);

  if (typeof performance !== 'undefined' && performance.mark && performance.measure) {
    performance.mark(PERF_MARK_END);
    try {
      performance.measure(PERF_MEASURE, PERF_MARK_START, PERF_MARK_END);
      const entries = performance.getEntriesByName(PERF_MEASURE);
      const last = entries[entries.length - 1];
      if (last) {
        // eslint-disable-next-line no-console
        console.info('[rasterTerrain] COG decode + mesh build ms:', Math.round(last.duration));
      }
    } catch (_) {
      /* ignore */
    }
  }

  return mesh;
}
